import { ICON_SIZE } from '@components/v2/constants';
import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Loading from '@components/v2/ui/Loading/Loading';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Snackbar as MuiSnackbar } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CircleAlert, CircleCheck, X } from 'lucide-react';
import type { ElementType } from 'react';
import { SnackbarTestIds } from '../../../../utils/testIds/snackbar.testIds';

export type SnackbarType = 'success' | 'error' | 'warning' | 'info';

interface SnackbarProps {
  open: boolean;
  type: SnackbarType;
  onClose: () => void;
  title?: string;
  message?: string;
  autoHideDuration?: number | null;
  loading?: boolean;
}

const AUTO_HIDE_DURATION = 5000;
const MESSAGE_MAX_WIDTH = 240;
const CLOSE_SLOT_SIZE = 28;

const ICON_BY_TYPE: Record<SnackbarType, ElementType> = {
  success: CircleCheck,
  error: CircleAlert,
  warning: CircleAlert,
  info: CircleAlert,
};

const Snackbar = ({
  open,
  type,
  onClose,
  title,
  message,
  autoHideDuration = AUTO_HIDE_DURATION,
  loading = false,
}: SnackbarProps) => {
  const displayTitle = title || message;
  const displayMessage = title ? message : undefined;

  if (!displayTitle) {
    return null;
  }

  return (
    <MuiSnackbar
      disableWindowBlurListener
      open={open}
      autoHideDuration={loading ? null : autoHideDuration}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={(theme) => ({ zIndex: theme.zIndex.modal + 1 })}
      onClose={(_, reason) => {
        if (reason === 'timeout') {
          onClose();
        }
      }}
    >
      <Box
        role={loading ? 'status' : 'alert'}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'flex-start',
          width: '100%',
          maxWidth: 420,
          p: 0.75,
          borderRadius: `${theme.shape.borderRadiusXl}px`,
          backgroundColor: theme.palette[type].light,
          boxShadow: `inset 0 0 0 0.5px ${alpha(theme.palette[type].main, 0.2)}, ${theme.shadows[1]}`,
        })}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1, minWidth: 0, p: 0.75 }}
        >
          <Box
            component="span"
            sx={(theme) => ({
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              width: ICON_SIZE.md,
              height: ICON_SIZE.md,
              color: theme.palette[type].dark,
            })}
          >
            {loading ? (
              <Loading size={ICON_SIZE.sm} color="inherit" />
            ) : (
              <Icon icon={ICON_BY_TYPE[type]} size="md" />
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              minWidth: 0,
              wordBreak: 'break-word',
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.primary"
              testId={SnackbarTestIds.TITLE}
              sx={{ lineHeight: 1.2 }}
            >
              {displayTitle}
            </Typography>
            {displayMessage && (
              <Typography
                variant="body2"
                color="text.secondary"
                testId={SnackbarTestIds.MESSAGE}
                sx={{ maxWidth: MESSAGE_MAX_WIDTH }}
              >
                {displayMessage}
              </Typography>
            )}
          </Box>
        </Box>

        {!loading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              width: CLOSE_SLOT_SIZE,
              height: CLOSE_SLOT_SIZE,
            }}
          >
            <IconButton
              icon={X}
              size="small"
              shape="round"
              ariaLabel="close"
              onClick={onClose}
              sx={{ '&:hover': { backgroundColor: 'transparent' } }}
            />
          </Box>
        )}
      </Box>
    </MuiSnackbar>
  );
};

export default Snackbar;
